import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { toChannelRequest } from './catalogModel';
import { CommunicationConflict } from './CommunicationConflict';
import { CommunicationEditor } from './CommunicationEditor';
import { CommunicationJourney } from './CommunicationJourney';
import { CommunicationUnsavedGuard } from './CommunicationUnsavedGuard';
import { EditorSaveBar } from './EditorSaveBar';
import { catalogIssues } from './editorModel';
import { changeSummary, journeyOccasions, journeyTitle } from './journeyModel';
import { MessagePreview } from './MessagePreview';
import { MessageWorkspace } from './MessageWorkspace';
import { exampleValues, MESSAGE_PRESENTATION } from './messagePresentation';
import { messagesSave } from './messagesSave';
import { MobileMessageEditor } from './MobileMessageEditor';
import { sampleValues } from './previewHighlight';
import type { useCommunicationCatalog } from './useCommunicationCatalog';
import { useMessagePreview } from './useMessagePreview';
import { useMessagesView } from './useMessagesView';

type Catalog = ReturnType<typeof useCommunicationCatalog>;

/** Die Nachrichten-Werkstatt: Gastreise, Editor und Vorschau — auf dem Telefon im Vollbild. */
export function MessagesArea(props: { catalog: Catalog; editable: boolean; objectName?: string }) {
  const { catalog, editable } = props;
  const draft = catalog.draft!;
  const baseline = catalog.baseline!;
  const view = useMessagesView();
  const phone = useMediaQuery('(max-width: 639px)');
  const issues = catalogIssues(draft);
  const message = draft.messages.find((item) => item.key === view.selected) ?? draft.messages[0]!;
  const channel = message.channels.some((item) => item.channel === view.channel)
    ? view.channel
    : message.channels[0]!.channel;
  const content = message.channels.find((item) => item.channel === channel)!;
  const blocked = issues.find((issue) => issue.key === message.key && issue.channel === channel);
  const preview = useMessagePreview(
    catalog.groupId,
    { key: message.key, channel, content: toChannelRequest(content) },
    blocked?.message ?? null,
  );
  const saving = messagesSave(catalog, view, issues, phone, editable);
  const presentation = MESSAGE_PRESENTATION[message.key];

  const editor = (
    <CommunicationEditor
      message={message}
      saved={baseline.messages.find((item) => item.key === message.key)}
      editable={editable}
      showHeader={!phone}
      channel={channel}
      lang={view.lang}
      pending={catalog.pending}
      examples={exampleValues(props.objectName)}
      focus={view.focus}
      onFocusHandled={view.clearFocus}
      onChannelChange={view.setChannel}
      onLangChange={view.setLang}
      onContentChange={(patch) => catalog.changeChannel(message.key, channel, patch)}
      onResetChannel={() => catalog.restoreChannel(message.key, channel)}
    />
  );
  const previewPane = (
    <MessagePreview
      channel={channel}
      {...preview}
      draft={catalog.pending.some(
        (change) => change.key === message.key && change.channel === channel,
      )}
      values={sampleValues(props.objectName)}
      guestLang={view.guestLang}
      onGuestLangChange={view.setGuestLang}
      frameClass={phone ? 'h-[62dvh]' : undefined}
    />
  );

  return (
    <>
      {catalog.conflict && (
        <CommunicationConflict
          dirty={catalog.dirty}
          reloadFailed={catalog.reloadFailed}
          onReload={() => void catalog.reload()}
        />
      )}
      <CommunicationJourney
        occasions={journeyOccasions(draft, baseline, issues)}
        selected={message.key}
        onSelect={(key) => {
          view.setSelected(key);
          if (phone) view.setPhoneEditorOpen(true);
        }}
        editable={editable}
        busy={catalog.save.isPending}
        pending={catalog.switches.pending}
        outcome={catalog.switches.outcome}
        onSwitch={catalog.switches.change}
        onUndo={catalog.switches.undo}
      />
      {phone ? (
        view.phoneEditorOpen && (
          <MobileMessageEditor
            title={journeyTitle(message.key)}
            subtitle={`${presentation.when}${message.isRequired ? ' · Pflicht' : ''}`}
            editor={editor}
            preview={previewPane}
            pane={view.pane}
            onPaneChange={view.setPane}
            onJump={() => saving.failure?.onJump?.()}
            save={saving.sheet}
          />
        )
      ) : (
        <MessageWorkspace
          editor={editor}
          preview={previewPane}
          view={view.pane}
          onViewChange={view.setPane}
        />
      )}
      {editable && !(phone && view.phoneEditorOpen) && (
        <EditorSaveBar
          count={catalog.pending.length}
          summary={changeSummary(catalog.pending)}
          saving={catalog.save.isPending}
          blocked={!!catalog.switches.pending}
          failure={saving.failure}
          onDiscard={saving.discard}
          onSave={saving.save}
        />
      )}
      <CommunicationUnsavedGuard dirty={catalog.dirty} />
    </>
  );
}
